import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Bot,
  ChevronDown,
  ChevronUp,
  CircleStop,
  ExternalLink,
  MessageSquareText,
  Mic,
  Pause,
  Play,
  RotateCcw,
  Send,
  Sparkles,
  Volume2,
  VolumeX,
  X
} from 'lucide-react';

interface ConciergeDockProps {
  enabled: boolean;
  onNavigate: (tab: string) => void;
  onOpenFullConcierge: () => void;
}

const CONTACT_REPLY = 'You can call NexoraX Digital free on 08000 622827, email support@nexoraxdigital.co.uk, or use info@nexoraxdigital.co.uk for general enquiries.';

export function NexoraXConciergeDock({ enabled, onNavigate, onOpenFullConcierge }: ConciergeDockProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const introAttemptedRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [query, setQuery] = useState('');
  const [reply, setReply] = useState('Welcome. I can introduce NexoraX, explain our services, help you contact us, or guide you through login and registration.');
  const [listening, setListening] = useState(false);
  const [pastHero, setPastHero] = useState(false);

  const stopAllSpeech = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setPlaying(false);
  };

  const playIntroduction = async (restart = false) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (restart) audio.currentTime = 0;
    audio.muted = muted;
    try {
      await audio.play();
      setPlaying(true);
      setAutoplayBlocked(false);
    } catch {
      setPlaying(false);
      setAutoplayBlocked(true);
    }
  };

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > 220);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!enabled || dismissed || introAttemptedRef.current) return;
    introAttemptedRef.current = true;
    const timer = window.setTimeout(() => void playIntroduction(true), 650);
    return () => window.clearTimeout(timer);
  }, [enabled, dismissed]);

  useEffect(() => {
    if (!enabled || !autoplayBlocked) return;
    const retryAfterGesture = () => void playIntroduction(true);
    window.addEventListener('pointerdown', retryAfterGesture, { once: true });
    window.addEventListener('keydown', retryAfterGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', retryAfterGesture);
      window.removeEventListener('keydown', retryAfterGesture);
    };
  }, [enabled, autoplayBlocked]);

  useEffect(() => {
    if (!enabled) stopAllSpeech();
    return () => stopAllSpeech();
  }, [enabled]);

  const speakReply = (text: string) => {
    if (muted || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-GB';
    utterance.rate = 0.96;
    const voices = window.speechSynthesis.getVoices();
    utterance.voice = voices.find((voice) => voice.lang.toLowerCase().startsWith('en-gb') && /female|susan|serena|sonia/i.test(voice.name))
      || voices.find((voice) => voice.lang.toLowerCase().startsWith('en-gb'))
      || null;
    window.speechSynthesis.speak(utterance);
  };

  const navigate = (tab: string, message: string) => {
    setReply(message);
    speakReply(message);
    window.setTimeout(() => onNavigate(tab), 350);
  };

  const handleQuestion = (rawQuestion: string) => {
    const text = rawQuestion.trim().toLowerCase();
    if (!text) return;
    setQuery('');

    if (/(contact|phone|telephone|email|call|speak to someone)/.test(text)) {
      navigate('Contact', CONTACT_REPLY);
    } else if (/(about|who are you|company|nexorax)/.test(text)) {
      navigate('About', 'NexoraX Digital builds websites, software, mobile experiences, automation and AI systems for modern businesses. I am opening our company story.');
    } else if (/(service|software|website|web design|mobile app|automation|development)/.test(text)) {
      navigate('Services / Solutions', 'I am opening our services and solutions so you can explore web development, software, automation and AI capabilities.');
    } else if (/(price|pricing|plan|package|cost|tier)/.test(text)) {
      navigate('Tiers', 'I am opening the NexoraX plans and service tiers. Our team can also prepare a tailored quotation for your business.');
    } else if (/(login|log in|sign in|account)/.test(text)) {
      navigate('Login', 'I am opening the secure account gateway. Choose sign in if you already have an account.');
    } else if (/(sign up|signup|register|create account|join)/.test(text)) {
      navigate('Login', 'I am opening the secure account gateway. Select the registration option to create your NexoraX account.');
    } else if (/(marketplace|template|digital product)/.test(text)) {
      navigate('NexoraX Marketplace', 'I am opening the NexoraX Marketplace, where you can explore digital products, templates and deployable solutions.');
    } else if (/(voice|assistant|concierge|matrix)/.test(text)) {
      const message = 'This concierge demonstrates the NexoraX Voice Matrix: branded welcome audio, spoken guidance, visitor intent routing and a full conversational AI workspace.';
      setReply(message);
      speakReply(message);
    } else {
      const message = 'I can guide you through services, pricing, our company, contact details, login, registration and the marketplace. Open the Full AI Concierge for a deeper conversation.';
      setReply(message);
      speakReply(message);
    }
  };

  const startListening = () => {
    const Recognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!Recognition) {
      setReply('Voice recognition is not supported in this browser. Please type your question below.');
      return;
    }
    const recognition = new Recognition();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => {
      setListening(false);
      setReply('I could not hear that clearly. Please try again or type your question.');
    };
    recognition.onresult = (event: any) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      setQuery(transcript);
      handleQuestion(transcript);
    };
    recognition.start();
  };

  if (!enabled || dismissed || pastHero) return null;

  return (
    <div className="absolute bottom-3 right-3 z-[9000] w-[min(390px,calc(100vw-24px))] text-left sm:bottom-5 sm:right-5 md:absolute md:bottom-auto md:left-1/2 md:right-auto md:top-[clamp(150px,21vh,235px)] md:w-[min(920px,calc(100vw-40px))] md:-translate-x-1/2">
      <audio
        ref={audioRef}
        src="/voice1.wav"
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      <motion.section
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="overflow-hidden rounded-[18px] border border-white/20 bg-[#030816]/35 shadow-[0_16px_52px_rgba(0,0,0,0.38),inset_0_1px_0_rgba(255,255,255,0.16),0_0_30px_rgba(0,240,255,0.08)] backdrop-blur-2xl"
        aria-label="NexoraX AI concierge"
      >
        <div className="relative flex items-center gap-3 border-b border-white/10 bg-white/[0.035] px-4 py-3 before:pointer-events-none before:absolute before:inset-x-5 before:top-0 before:h-px before:bg-white/25">
          <div className="relative grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-cyan-300/35 bg-black/55 text-cyan-300">
            <Bot className="h-5 w-5" />
            <span className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-[#020711] ${playing ? 'bg-emerald-400 animate-pulse' : 'bg-cyan-400'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-cyan-300">
              <Sparkles className="h-3 w-3" /> Live Website Concierge
            </div>
            <h2 className="truncate text-sm font-black text-white">NexoraX Voice Matrix</h2>
            <p className="text-[9px] uppercase tracking-wider text-slate-400">Welcome, guidance and AI assistance</p>
          </div>
          <button onClick={() => playing ? audioRef.current?.pause() : void playIntroduction(false)} className="rounded-md border border-cyan-300/25 bg-cyan-300/10 p-2 text-cyan-100 hover:bg-cyan-300/20" title={playing ? 'Pause welcome voice' : 'Play welcome voice'}>
            {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4" fill="currentColor" />}
          </button>
          <button onClick={() => setExpanded((value) => !value)} className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" title={expanded ? 'Minimize concierge' : 'Expand concierge'}>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <button onClick={() => { stopAllSpeech(); setDismissed(true); }} className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" title="Close concierge">
            <X className="h-4 w-4" />
          </button>
        </div>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div className="space-y-3 p-4">
                {autoplayBlocked && !playing && (
                  <button onClick={() => void playIntroduction(true)} className="flex w-full items-center justify-center gap-2 rounded-md border border-amber-300/35 bg-amber-400/10 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-amber-200 hover:bg-amber-400/20">
                    <Play className="h-3.5 w-3.5" fill="currentColor" /> Enable Welcome Voice
                  </button>
                )}

                <div className="rounded-md border border-white/10 bg-black/45 p-3">
                  <div className="mb-2 flex h-7 items-center justify-center gap-1 overflow-hidden" aria-hidden="true">
                    {Array.from({ length: 24 }).map((_, index) => (
                      <span key={index} className={`w-0.5 rounded-full bg-gradient-to-t from-cyan-500 to-fuchsia-400 ${playing ? 'animate-pulse' : ''}`} style={{ height: playing ? `${8 + ((index * 11) % 20)}px` : '3px', animationDelay: `${index * 45}ms` }} />
                    ))}
                  </div>
                  <p className="text-[11px] leading-relaxed text-slate-300" aria-live="polite">{reply}</p>
                </div>

                <div className="grid grid-cols-5 gap-1.5">
                  <button onClick={() => playing ? audioRef.current?.pause() : void playIntroduction(false)} className="grid place-items-center rounded-md border border-cyan-400/20 bg-cyan-400/10 py-2 text-cyan-200 hover:bg-cyan-400/20" title={playing ? 'Pause' : 'Play'}>
                    {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4" fill="currentColor" />}
                  </button>
                  <button onClick={() => void playIntroduction(true)} className="grid place-items-center rounded-md border border-white/10 bg-white/5 py-2 text-slate-300 hover:bg-white/10" title="Restart welcome">
                    <RotateCcw className="h-4 w-4" />
                  </button>
                  <button onClick={() => { const next = !muted; setMuted(next); if (audioRef.current) audioRef.current.muted = next; if (next && 'speechSynthesis' in window) window.speechSynthesis.cancel(); }} className="grid place-items-center rounded-md border border-white/10 bg-white/5 py-2 text-slate-300 hover:bg-white/10" title={muted ? 'Unmute' : 'Mute'}>
                    {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </button>
                  <button onClick={stopAllSpeech} className="grid place-items-center rounded-md border border-white/10 bg-white/5 py-2 text-slate-300 hover:bg-white/10" title="Stop voice">
                    <CircleStop className="h-4 w-4" />
                  </button>
                  <button onClick={startListening} className={`grid place-items-center rounded-md border py-2 ${listening ? 'border-rose-400/50 bg-rose-400/15 text-rose-200' : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'}`} title="Ask by voice">
                    <Mic className={`h-4 w-4 ${listening ? 'animate-pulse' : ''}`} />
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    ['Services', 'Services / Solutions'],
                    ['About', 'About'],
                    ['Contact', 'Contact'],
                    ['Login', 'Login']
                  ].map(([label, tab]) => (
                    <button key={label} onClick={() => onNavigate(tab)} className="rounded-md border border-white/10 bg-white/5 px-1 py-2 text-[9px] font-bold uppercase tracking-wider text-slate-300 hover:border-cyan-400/30 hover:text-cyan-200">
                      {label}
                    </button>
                  ))}
                </div>

                <form onSubmit={(event) => { event.preventDefault(); handleQuestion(query); }} className="flex gap-2">
                  <label className="relative flex-1">
                    <MessageSquareText className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                    <input value={query} onChange={(event) => setQuery(event.target.value)} className="w-full rounded-md border border-white/10 bg-black/55 py-2.5 pl-9 pr-3 text-xs text-white outline-none placeholder:text-slate-600 focus:border-cyan-400/40" placeholder="Ask about NexoraX..." aria-label="Ask the NexoraX concierge" />
                  </label>
                  <button type="submit" className="rounded-md bg-cyan-400 px-3 text-black hover:bg-white" title="Send question">
                    <Send className="h-4 w-4" />
                  </button>
                </form>

                <button onClick={() => { stopAllSpeech(); onOpenFullConcierge(); }} className="flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-500 px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.14em] text-black hover:brightness-110">
                  <ExternalLink className="h-3.5 w-3.5" /> Open Full AI Concierge
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>
    </div>
  );

}
